import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Mail,
  FileText,
  Clock,
  BarChart3,
  Plus,
  Send,
  Users,
  Pencil,
  Trash2,
  Copy,
  Eye,
  Search,
  Filter,
  Calendar,
  ChevronRight,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Monitor,
  Settings,
  Key,
  User,
  Save,
  EyeOff,
  Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import EmailEditor from '../components/EmailEditor';
import EmailPreview from '../components/EmailPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailMarketing = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'templates');
  const [loading, setLoading] = useState(true);
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    html_body: '',
    description: ''
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Campaigns state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignTotal, setCampaignTotal] = useState(0);
  
  // Compose state
  const [composeDialogOpen, setComposeDialogOpen] = useState(false);
  const [senders, setSenders] = useState([]);
  const [courses, setCourses] = useState([]);
  const [trainingCenters, setTrainingCenters] = useState([]);
  const [composeForm, setComposeForm] = useState({
    name: '',
    subject: '',
    html_body: '',
    sender_email: '',
    recipient_filter: {
      country: null,
      training_center_ids: [],
      course_ids: [],
      certificate_expiring_days: null
    },
    scheduled_at: ''
  });
  const [recipientPreview, setRecipientPreview] = useState(null);
  const [previewingRecipients, setPreviewingRecipients] = useState(false);
  const [sendingCampaign, setSendingCampaign] = useState(false);
  
  // Email preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  
  // Campaign details dialog
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignStats, setCampaignStats] = useState(null);
  
  // Test email dialog
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  
  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  
  // Campaign recipients detail
  const [campaignRecipients, setCampaignRecipients] = useState(null);
  const [recipientFilter, setRecipientFilter] = useState('all');
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  
  // Marketing Settings state
  const [marketingSettings, setMarketingSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    api_key: '',
    from_email: '',
    from_name: ''
  });
  const [apiKeyChanged, setApiKeyChanged] = useState(false);
  
  // Sender management state
  const [marketingSenders, setMarketingSenders] = useState([]);
  const [senderDialogOpen, setSenderDialogOpen] = useState(false);
  const [editingSender, setEditingSender] = useState(null);
  const [senderForm, setSenderForm] = useState({ email: '', name: '', is_default: false });
  const [savingSender, setSavingSender] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        axios.get(`${API_URL}/api/email-marketing/templates`),
        axios.get(`${API_URL}/api/email-marketing/campaigns?page=${campaignPage}`),
        axios.get(`${API_URL}/api/email-marketing/senders`),
        axios.get(`${API_URL}/api/courses?page_size=1000`),
        axios.get(`${API_URL}/api/training-centers?page_size=100`)
      ]);
      const [templatesRes, campaignsRes, sendersRes, coursesRes, centersRes] = results.map(r => r.status === 'fulfilled' ? r.value : null);
      results.forEach((r, i) => r.status === 'rejected' && console.error('Email marketing load failed:', i, r.reason));

      if (templatesRes) setTemplates(templatesRes.data);
      if (campaignsRes) {
        setCampaigns(campaignsRes.data.items || []);
        setCampaignTotal(campaignsRes.data.total || 0);
      }
      if (sendersRes) setSenders(sendersRes.data);
      if (coursesRes) setCourses(coursesRes.data.items || coursesRes.data || []);
      if (centersRes) setTrainingCenters(centersRes.data.items || centersRes.data || []);

      const defaultSender = sendersRes?.data.find(s => s.default);
      if (defaultSender) {
        setComposeForm(prev => ({ ...prev, sender_email: defaultSender.email }));
      }
      if (results.some(r => r.status === 'rejected')) toast.error('Some data failed to load');
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [campaignPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Template functions
  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.name || !templateForm.subject || !templateForm.html_body) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSavingTemplate(true);
    try {
      if (editingTemplate) {
        await axios.put(`${API_URL}/api/email-marketing/templates/${editingTemplate.id}`, templateForm);
        toast.success('Template updated');
      } else {
        await axios.post(`${API_URL}/api/email-marketing/templates`, templateForm);
        toast.success('Template created');
      }
      setTemplateDialogOpen(false);
      setEditingTemplate(null);
      setTemplateForm({ name: '', subject: '', html_body: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Delete template "${template.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/email-marketing/templates/${template.id}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      await axios.post(`${API_URL}/api/email-marketing/templates/${template.id}/duplicate`);
      toast.success('Template duplicated');
      fetchData();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  const handleUseTemplate = (template) => {
    setComposeForm(prev => ({
      ...prev,
      name: template.name,
      subject: template.subject,
      html_body: template.html_body
    }));
    setActiveTab('compose');
  };

  // Recipient preview
  const previewRecipients = async () => {
    setPreviewingRecipients(true);
    try {
      const response = await axios.post(`${API_URL}/api/email-marketing/recipients/preview`, {
        recipient_filter: composeForm.recipient_filter
      });
      setRecipientPreview(response.data);
    } catch (error) {
      toast.error('Failed to preview recipients');
    } finally {
      setPreviewingRecipients(false);
    }
  };

  // Send campaign
  const handleSendCampaign = async (e) => {
    e.preventDefault();
    if (!composeForm.name || !composeForm.subject || !composeForm.html_body) {
      toast.error('Please fill in campaign name, subject, and email body');
      return;
    }
    
    if (!recipientPreview || recipientPreview.count === 0) {
      toast.error('Please preview and confirm recipients first');
      return;
    }
    
    setSendingCampaign(true);
    try {
      const payload = {
        ...composeForm,
        scheduled_at: composeForm.scheduled_at || null
      };
      
      await axios.post(`${API_URL}/api/email-marketing/campaigns`, payload);
      toast.success(composeForm.scheduled_at ? 'Campaign scheduled!' : 'Campaign sending started!');
      setComposeForm({
        name: '',
        subject: '',
        html_body: '',
        sender_email: senders.find(s => s.default)?.email || '',
        recipient_filter: {
          country: null,
          training_center_ids: [],
          course_ids: [],
          certificate_expiring_days: null
        },
        scheduled_at: ''
      });
      setRecipientPreview(null);
      setActiveTab('history');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send campaign');
    } finally {
      setSendingCampaign(false);
    }
  };

  // Send test email
  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    
    setSendingTest(true);
    try {
      // Send with query params for simple fields, body for html_body
      const response = await axios.post(
        `${API_URL}/api/email-marketing/send-test?subject=${encodeURIComponent(composeForm.subject)}&test_email=${encodeURIComponent(testEmail)}&sender_email=${encodeURIComponent(composeForm.sender_email)}`,
        { html_body: composeForm.html_body },
        { headers: { 'Content-Type': 'application/json' } }
      );
      
      if (response.data.status === 'success') {
        toast.success(`Test email sent to ${testEmail}`);
        setTestEmailDialogOpen(false);
      } else {
        toast.error(response.data.message || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Test email error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  // View campaign stats
  const viewCampaignStats = async (campaign) => {
    setSelectedCampaign(campaign);
    try {
      const response = await axios.get(`${API_URL}/api/email-marketing/campaigns/${campaign.id}/stats`);
      setCampaignStats(response.data);
      // Also load recipients
      loadCampaignRecipients(campaign.id);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  // Load campaign recipients with engagement data
  const loadCampaignRecipients = async (campaignId, filter = 'all') => {
    setLoadingRecipients(true);
    try {
      const filterParam = filter !== 'all' ? `&filter_status=${filter}` : '';
      const response = await axios.get(`${API_URL}/api/email-marketing/campaigns/${campaignId}/recipients?page_size=100${filterParam}`);
      setCampaignRecipients(response.data);
    } catch (error) {
      console.error('Failed to load recipients:', error);
    } finally {
      setLoadingRecipients(false);
    }
  };

  // Load analytics overview
  const loadAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const response = await axios.get(`${API_URL}/api/email-marketing/analytics/overview`);
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  }, []);

  // Load analytics when tab changes
  useEffect(() => {
    if (activeTab === 'analytics' && !analyticsData) {
      loadAnalytics();
    }
  }, [activeTab, analyticsData, loadAnalytics]);

  // Load marketing settings and senders
  const loadMarketingSettings = useCallback(async () => {
    setLoadingSettings(true);
    try {
      const [settingsRes, sendersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/email-settings/marketing`),
        axios.get(`${API_URL}/api/admin/email-settings/marketing/senders`)
      ]);
      
      setMarketingSettings(settingsRes.data);
      setSettingsForm({
        api_key: '',
        from_email: settingsRes.data.from_email || '',
        from_name: settingsRes.data.from_name || ''
      });
      setMarketingSenders(sendersRes.data);
    } catch (error) {
      console.error('Failed to load marketing settings:', error);
      toast.error('Failed to load marketing settings');
    } finally {
      setLoadingSettings(false);
    }
  }, []);

  // Load marketing settings when Settings tab is opened
  useEffect(() => {
    if (activeTab === 'settings' && !marketingSettings) {
      loadMarketingSettings();
    }
  }, [activeTab, marketingSettings, loadMarketingSettings]);

  // Save marketing API settings
  const handleSaveMarketingSettings = async () => {
    setSavingSettings(true);
    try {
      const updateData = {
        from_email: settingsForm.from_email,
        from_name: settingsForm.from_name
      };
      
      if (apiKeyChanged && settingsForm.api_key) {
        updateData.api_key = settingsForm.api_key;
      }
      
      const response = await axios.put(`${API_URL}/api/admin/email-settings/marketing`, updateData);
      setMarketingSettings(response.data);
      setSettingsForm(prev => ({ ...prev, api_key: '' }));
      setApiKeyChanged(false);
      toast.success('Marketing email settings saved');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Sender CRUD operations
  const handleSaveSender = async (e) => {
    e.preventDefault();
    if (!senderForm.email || !senderForm.name) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSavingSender(true);
    try {
      if (editingSender) {
        await axios.put(`${API_URL}/api/admin/email-settings/marketing/senders/${editingSender.id}`, senderForm);
        toast.success('Sender updated');
      } else {
        await axios.post(`${API_URL}/api/admin/email-settings/marketing/senders`, senderForm);
        toast.success('Sender added');
      }
      
      setSenderDialogOpen(false);
      setEditingSender(null);
      setSenderForm({ email: '', name: '', is_default: false });
      
      // Refresh senders list
      const sendersRes = await axios.get(`${API_URL}/api/admin/email-settings/marketing/senders`);
      setMarketingSenders(sendersRes.data);
      // Also refresh the main senders list used in compose
      setSenders(sendersRes.data.map(s => ({ ...s, default: !!s.is_default })));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save sender');
    } finally {
      setSavingSender(false);
    }
  };

  const handleDeleteSender = async (sender) => {
    if (!window.confirm(`Delete sender "${sender.name}" (${sender.email})?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/email-settings/marketing/senders/${sender.id}`);
      toast.success('Sender deleted');
      
      // Refresh senders list
      const sendersRes = await axios.get(`${API_URL}/api/admin/email-settings/marketing/senders`);
      setMarketingSenders(sendersRes.data);
      setSenders(sendersRes.data.map(s => ({ ...s, default: !!s.is_default })));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sender');
    }
  };

  const handleSetDefaultSender = async (sender) => {
    try {
      await axios.post(`${API_URL}/api/admin/email-settings/marketing/senders/${sender.id}/set-default`);
      toast.success(`Default sender set to ${sender.email}`);
      
      // Refresh senders list
      const sendersRes = await axios.get(`${API_URL}/api/admin/email-settings/marketing/senders`);
      setMarketingSenders(sendersRes.data);
      setSenders(sendersRes.data.map(s => ({ ...s, default: !!s.is_default })));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set default sender');
    }
  };

  // Cancel scheduled campaign
  const handleCancelCampaign = async (campaign) => {
    if (!window.confirm(`Cancel scheduled campaign "${campaign.name}"?`)) return;
    try {
      await axios.post(`${API_URL}/api/email-marketing/campaigns/${campaign.id}/cancel`);
      toast.success('Campaign cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel campaign');
    }
  };

  // Send scheduled campaign immediately
  const handleSendNow = async (campaign) => {
    if (!window.confirm(`Send campaign "${campaign.name}" immediately?`)) return;
    try {
      await axios.post(`${API_URL}/api/email-marketing/campaigns/${campaign.id}/send-now`);
      toast.success('Campaign is being sent!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send campaign');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-slate-100 text-slate-700', icon: FileText },
      scheduled: { color: 'bg-blue-100 text-blue-700', icon: Clock },
      sending: { color: 'bg-yellow-100 text-yellow-700', icon: Loader2 },
      sent: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      failed: { color: 'bg-red-100 text-red-700', icon: XCircle },
      cancelled: { color: 'bg-slate-100 text-slate-500', icon: XCircle }
    };
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#2C9DE1]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link 
            to="/admin-settings" 
            className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Settings
          </Link>
        </div>
      </div>
      
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Email Marketing
        </h1>
        <p className="text-slate-500 mt-1">
          Send promotional emails to students who have opted in
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-2xl grid-cols-5">
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="compose" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Compose
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            History
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">
              Create reusable email templates for your campaigns
            </p>
            <Button
              onClick={() => {
                setEditingTemplate(null);
                setTemplateForm({ name: '', subject: '', html_body: '', description: '' });
                setTemplateDialogOpen(true);
              }}
              className="bg-[#2C9DE1] hover:bg-[#1A7BB8]"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          </div>

          {templates.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No templates yet. Create your first template to get started.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {templates.map((template) => (
                <Card key={template.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="truncate">{template.name}</span>
                    </CardTitle>
                    <CardDescription className="text-xs truncate">
                      Subject: {template.subject}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {template.description && (
                      <p className="text-xs text-slate-500 mb-3 line-clamp-2">{template.description}</p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUseTemplate(template)}
                        className="flex-1"
                      >
                        <Send className="w-3 h-3 mr-1" />
                        Use
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingTemplate(template);
                          setTemplateForm({
                            name: template.name,
                            subject: template.subject,
                            html_body: template.html_body,
                            description: template.description || ''
                          });
                          setTemplateDialogOpen(true);
                        }}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDuplicateTemplate(template)}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDeleteTemplate(template)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Compose Tab */}
        <TabsContent value="compose" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main compose form */}
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Compose Email</CardTitle>
                  <CardDescription>Create your marketing email</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Campaign Name *</Label>
                      <Input
                        value={composeForm.name}
                        onChange={(e) => setComposeForm({ ...composeForm, name: e.target.value })}
                        placeholder="e.g., March Newsletter"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Sender Email</Label>
                      <Select
                        value={composeForm.sender_email}
                        onValueChange={(value) => setComposeForm({ ...composeForm, sender_email: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select sender" />
                        </SelectTrigger>
                        <SelectContent>
                          {senders.map((sender) => (
                            <SelectItem key={sender.email} value={sender.email}>
                              {sender.name} ({sender.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Subject Line *</Label>
                    <Input
                      value={composeForm.subject}
                      onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })}
                      placeholder="Email subject"
                    />
                    <p className="text-xs text-slate-500">
                      Available tokens: {'{{first_name}}'}, {'{{last_name}}'}, {'{{full_name}}'}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Email Body *</Label>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setPreviewOpen(true)}
                        disabled={!composeForm.html_body}
                        className="h-7"
                      >
                        <Monitor className="w-3 h-3 mr-1" />
                        Preview
                      </Button>
                    </div>
                    <EmailEditor
                      value={composeForm.html_body}
                      onChange={(html) => setComposeForm({ ...composeForm, html_body: html })}
                      placeholder="Start writing your email content..."
                    />
                    <p className="text-xs text-slate-500">
                      Use the toolbar to format text. Header, footer, and unsubscribe link will be added automatically.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Schedule (Optional)</Label>
                    <Input
                      type="datetime-local"
                      value={composeForm.scheduled_at}
                      onChange={(e) => setComposeForm({ ...composeForm, scheduled_at: e.target.value })}
                    />
                    <p className="text-xs text-slate-500">
                      Leave empty to send immediately
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recipients sidebar */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Recipients
                  </CardTitle>
                  <CardDescription>Filter who receives this email</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Country</Label>
                    <Select
                      value={composeForm.recipient_filter.country || 'all'}
                      onValueChange={(value) => setComposeForm({
                        ...composeForm,
                        recipient_filter: {
                          ...composeForm.recipient_filter,
                          country: value === 'all' ? null : value
                        }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All countries" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Countries</SelectItem>
                        <SelectItem value="Cyprus">Cyprus</SelectItem>
                        <SelectItem value="Greece">Greece</SelectItem>
                        <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                        <SelectItem value="Germany">Germany</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm">Training Center</Label>
                    <Select
                      value={composeForm.recipient_filter.training_center_ids?.[0] || 'all'}
                      onValueChange={(value) => setComposeForm({
                        ...composeForm,
                        recipient_filter: {
                          ...composeForm.recipient_filter,
                          training_center_ids: value === 'all' ? [] : [value]
                        }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All centers" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Training Centers</SelectItem>
                        {trainingCenters.map((tc) => (
                          <SelectItem key={tc.id} value={tc.id}>{tc.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm">Courses Certified In</Label>
                    <Select
                      value={composeForm.recipient_filter.course_ids?.[0] || 'all'}
                      onValueChange={(value) => setComposeForm({
                        ...composeForm,
                        recipient_filter: {
                          ...composeForm.recipient_filter,
                          course_ids: value === 'all' ? [] : [value]
                        }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All courses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Courses</SelectItem>
                        {courses.map((course) => (
                          <SelectItem key={course.id} value={course.id}>{course.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm">Certificate Expiring</Label>
                    <Select
                      value={composeForm.recipient_filter.certificate_expiring_days?.toString() || 'none'}
                      onValueChange={(value) => setComposeForm({
                        ...composeForm,
                        recipient_filter: {
                          ...composeForm.recipient_filter,
                          certificate_expiring_days: value === 'none' ? null : parseInt(value)
                        }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="No expiry filter" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No expiry filter</SelectItem>
                        <SelectItem value="30">Within 30 days</SelectItem>
                        <SelectItem value="60">Within 60 days</SelectItem>
                        <SelectItem value="90">Within 90 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <Checkbox
                      data-testid="compose-exclude-failed"
                      checked={!!composeForm.recipient_filter.exclude_failed}
                      onCheckedChange={(checked) => {
                        setComposeForm({
                          ...composeForm,
                          recipient_filter: { ...composeForm.recipient_filter, exclude_failed: !!checked }
                        });
                        setRecipientPreview(null);
                      }}
                    />
                    <span>Exclude contacts whose emails failed before</span>
                  </label>
                  
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={previewRecipients}
                    disabled={previewingRecipients}
                    data-testid="compose-preview-recipients-btn"
                  >
                    {previewingRecipients ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Eye className="w-4 h-4 mr-2" />
                    )}
                    Preview Recipients
                  </Button>
                  
                  {recipientPreview && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="font-semibold text-lg text-[#2C9DE1]">
                        {recipientPreview.count} recipients
                      </p>
                      <p className="text-xs text-slate-500 mb-2">
                        Students with email permission enabled
                      </p>
                      {recipientPreview.recipients.slice(0, 5).map((r, i) => (
                        <p key={i} className="text-xs text-slate-600 truncate">
                          {r.name} - {r.email}
                        </p>
                      ))}
                      {recipientPreview.has_more && (
                        <p className="text-xs text-slate-400 mt-1">
                          +{recipientPreview.count - 5} more...
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Action buttons */}
              <div className="space-y-2">
                <Button
                  className="w-full bg-[#2C9DE1] hover:bg-[#1A7BB8]"
                  onClick={handleSendCampaign}
                  disabled={sendingCampaign || !recipientPreview?.count}
                >
                  {sendingCampaign ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : composeForm.scheduled_at ? (
                    <Clock className="w-4 h-4 mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {composeForm.scheduled_at ? 'Schedule Campaign' : 'Send Now'}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setTestEmailDialogOpen(true)}
                  disabled={!composeForm.subject || !composeForm.html_body}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Test Email
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <p className="text-sm text-slate-500">
            View sent campaigns and their performance
          </p>
          
          {campaigns.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BarChart3 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No campaigns sent yet.</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Recipients</TableHead>
                    <TableHead className="text-center">Opened</TableHead>
                    <TableHead className="text-center">Clicked</TableHead>
                    <TableHead>Scheduled / Sent</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map((campaign) => (
                    <TableRow key={campaign.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{campaign.name}</p>
                          <p className="text-xs text-slate-500 truncate max-w-[200px]">
                            {campaign.subject}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                      <TableCell className="text-center">{campaign.recipient_count}</TableCell>
                      <TableCell className="text-center">
                        <span className="text-green-600">{campaign.opened_count}</span>
                        {campaign.sent_count > 0 && (
                          <span className="text-xs text-slate-400 ml-1">
                            ({Math.round(campaign.opened_count / campaign.sent_count * 100)}%)
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-blue-600">{campaign.clicked_count}</span>
                        {campaign.sent_count > 0 && (
                          <span className="text-xs text-slate-400 ml-1">
                            ({Math.round(campaign.clicked_count / campaign.sent_count * 100)}%)
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {campaign.status === 'scheduled' && campaign.scheduled_at ? (
                          <div>
                            <p className="text-blue-600 font-medium">
                              {new Date(campaign.scheduled_at).toLocaleDateString()}
                            </p>
                            <p className="text-xs">
                              {new Date(campaign.scheduled_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </p>
                          </div>
                        ) : campaign.sent_at ? (
                          new Date(campaign.sent_at).toLocaleDateString()
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/email-marketing/campaigns/${campaign.id}`)}
                            title="View Details"
                            data-testid={`campaign-view-${campaign.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {campaign.status === 'scheduled' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleSendNow(campaign)}
                                title="Send Now"
                                className="text-green-600 hover:text-green-700"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleCancelCampaign(campaign)}
                                title="Cancel"
                                className="text-red-600 hover:text-red-700"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          {loadingAnalytics ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-8 h-8 animate-spin text-[#2C9DE1]" />
            </div>
          ) : analyticsData ? (
            <>
              {/* Overview Stats Cards */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-slate-900">{analyticsData.total_campaigns}</p>
                      <p className="text-sm text-slate-500 mt-1">Total Campaigns</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-[#2C9DE1]">{analyticsData.total_sent}</p>
                      <p className="text-sm text-slate-500 mt-1">Emails Sent</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-green-600">{analyticsData.avg_open_rate}%</p>
                      <p className="text-sm text-green-700 mt-1">Avg Open Rate</p>
                      <p className="text-xs text-green-600 mt-1">{analyticsData.total_opened} opens</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-blue-600">{analyticsData.avg_click_rate}%</p>
                      <p className="text-sm text-blue-700 mt-1">Avg Click Rate</p>
                      <p className="text-xs text-blue-600 mt-1">{analyticsData.total_clicked} clicks</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Campaigns Performance */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Campaign Performance</CardTitle>
                  <CardDescription>Last 5 campaigns sent</CardDescription>
                </CardHeader>
                <CardContent>
                  {analyticsData.recent_campaigns.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No campaigns sent yet</p>
                  ) : (
                    <div className="space-y-4">
                      {analyticsData.recent_campaigns.map((campaign) => (
                        <div key={campaign.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                          <div className="flex-1">
                            <p className="font-medium">{campaign.name}</p>
                            <p className="text-xs text-slate-500">
                              {campaign.sent_count} sent • {campaign.sent_at ? new Date(campaign.sent_at).toLocaleDateString() : ''}
                            </p>
                          </div>
                          <div className="flex gap-8 items-center">
                            <div className="text-center">
                              <p className="text-lg font-bold text-green-600">{campaign.open_rate}%</p>
                              <p className="text-xs text-slate-500">Open Rate</p>
                            </div>
                            <div className="text-center">
                              <p className="text-lg font-bold text-blue-600">{campaign.click_rate}%</p>
                              <p className="text-xs text-slate-500">Click Rate</p>
                            </div>
                            <div className="w-32">
                              {/* Progress bar for open rate */}
                              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-green-500 rounded-full transition-all"
                                  style={{ width: `${Math.min(campaign.open_rate, 100)}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Benchmarks */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Industry Benchmarks</CardTitle>
                  <CardDescription>Compare your performance to industry standards</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-slate-600">Your Open Rate</span>
                        <span className="text-sm font-medium">{analyticsData.avg_open_rate}%</span>
                      </div>
                      <div className="h-3 bg-slate-200 rounded-full overflow-hidden relative">
                        <div 
                          className="h-full bg-green-500 rounded-full"
                          style={{ width: `${Math.min(analyticsData.avg_open_rate, 100)}%` }}
                        />
                        {/* Benchmark line at 20% (industry average) */}
                        <div className="absolute top-0 h-full w-0.5 bg-slate-400" style={{ left: '20%' }} />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Industry avg: ~20%</p>
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-slate-600">Your Click Rate</span>
                        <span className="text-sm font-medium">{analyticsData.avg_click_rate}%</span>
                      </div>
                      <div className="h-3 bg-slate-200 rounded-full overflow-hidden relative">
                        <div 
                          className="h-full bg-blue-500 rounded-full"
                          style={{ width: `${Math.min(analyticsData.avg_click_rate * 5, 100)}%` }}
                        />
                        {/* Benchmark line at 2.5% (industry average) */}
                        <div className="absolute top-0 h-full w-0.5 bg-slate-400" style={{ left: '12.5%' }} />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Industry avg: ~2.5%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Button variant="outline" onClick={loadAnalytics} className="w-full">
                <BarChart3 className="w-4 h-4 mr-2" />
                Refresh Analytics
              </Button>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <BarChart3 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Send your first campaign to see analytics</p>
                <Button 
                  className="mt-4 bg-[#2C9DE1] hover:bg-[#1A7BB8]"
                  onClick={() => setActiveTab('compose')}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Create Campaign
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          {loadingSettings ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-8 h-8 animate-spin text-[#2C9DE1]" />
            </div>
          ) : (
            <>
              {/* Marketing API Settings Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    <Key className="w-5 h-5 text-[#2C9DE1]" />
                    Marketing Email API Settings
                  </CardTitle>
                  <CardDescription>
                    Configure Resend API settings specifically for marketing emails. This is separate from transactional email settings.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* API Key */}
                  <div className="space-y-2">
                    <Label htmlFor="marketing_api_key" className="flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Resend API Key
                    </Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          id="marketing_api_key"
                          data-testid="marketing-api-key-input"
                          type={showApiKey ? 'text' : 'password'}
                          placeholder={marketingSettings?.api_key_set ? marketingSettings.api_key_masked : 'Enter API key...'}
                          value={settingsForm.api_key}
                          onChange={(e) => {
                            setSettingsForm({ ...settingsForm, api_key: e.target.value });
                            setApiKeyChanged(true);
                          }}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKey(!showApiKey)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">
                      {marketingSettings?.api_key_set 
                        ? 'API key is configured. Enter a new key to replace it.' 
                        : 'No API key configured. Enter your Resend API key for marketing emails.'}
                    </p>
                  </div>

                  {/* Last Updated Info */}
                  {marketingSettings?.updated_at && (
                    <div className="pt-4 border-t border-slate-200">
                      <p className="text-sm text-slate-500">
                        Last updated: {new Date(marketingSettings.updated_at).toLocaleString()} 
                        {marketingSettings.updated_by && ` by ${marketingSettings.updated_by}`}
                      </p>
                    </div>
                  )}

                  {/* Save Button */}
                  <div className="flex justify-end pt-2">
                    <Button
                      onClick={handleSaveMarketingSettings}
                      disabled={savingSettings}
                      className="bg-[#2C9DE1] hover:bg-[#1A7BB8]"
                      data-testid="save-api-settings-btn"
                    >
                      {savingSettings ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save API Settings
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Sender Addresses Card */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        <Users className="w-5 h-5 text-[#2C9DE1]" />
                        Sender Addresses
                      </CardTitle>
                      <CardDescription className="mt-1">
                        Manage sender addresses for marketing campaigns. The default sender is pre-selected when composing emails.
                      </CardDescription>
                    </div>
                    <Button
                      onClick={() => {
                        setEditingSender(null);
                        setSenderForm({ email: '', name: '', is_default: false });
                        setSenderDialogOpen(true);
                      }}
                      className="bg-[#2C9DE1] hover:bg-[#1A7BB8]"
                      data-testid="add-sender-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Sender
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {marketingSenders.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500">No sender addresses configured</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {marketingSenders.map((sender) => (
                        <div 
                          key={sender.id} 
                          className={`flex items-center justify-between p-4 rounded-lg border ${
                            sender.is_default ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              sender.is_default ? 'bg-blue-500' : 'bg-slate-400'
                            }`}>
                              <User className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-slate-900">{sender.name}</p>
                                {sender.is_default && (
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">
                                    <Star className="w-3 h-3 mr-1" />
                                    Default
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-500">{sender.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!sender.is_default && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleSetDefaultSender(sender)}
                                title="Set as default"
                                className="text-blue-600 hover:text-blue-700"
                                data-testid={`set-default-sender-${sender.id}`}
                              >
                                <Star className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingSender(sender);
                                setSenderForm({
                                  email: sender.email,
                                  name: sender.name,
                                  is_default: sender.is_default
                                });
                                setSenderDialogOpen(true);
                              }}
                              title="Edit"
                              data-testid={`edit-sender-${sender.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteSender(sender)}
                              title="Delete"
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-sender-${sender.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Info Card */}
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="pt-6">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-amber-900">Separate API Keys</h4>
                      <p className="text-sm text-amber-700 mt-1">
                        Marketing emails use a separate API key from transactional emails (account creation, password reset). 
                        This allows you to track costs and usage separately, and use different sending limits.
                      </p>
                      <p className="text-sm text-amber-700 mt-2">
                        To configure transactional email settings, go to{' '}
                        <button 
                          onClick={() => navigate('/email-settings')}
                          className="text-amber-800 underline hover:text-amber-900"
                        >
                          Email Settings
                        </button>.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingTemplate ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
            <DialogDescription>
              Save an email template for reuse in campaigns
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveTemplate} className="space-y-4">
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="e.g., Course Promotion"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={templateForm.description}
                onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                placeholder="Brief description of this template"
              />
            </div>
            <div className="space-y-2">
              <Label>Subject Line *</Label>
              <Input
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                placeholder="Email subject"
              />
            </div>
            <div className="space-y-2">
              <Label>Email Body *</Label>
              <EmailEditor
                value={templateForm.html_body}
                onChange={(html) => setTemplateForm({ ...templateForm, html_body: html })}
                placeholder="Start writing your template content..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTemplateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#2C9DE1] hover:bg-[#1A7BB8]" disabled={savingTemplate}>
                {savingTemplate && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingTemplate ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testEmailDialogOpen} onOpenChange={setTestEmailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Send Test Email
            </DialogTitle>
            <DialogDescription>
              Send a preview of this email to yourself
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Recipient Email Address</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your@email.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Sender Email</Label>
              <Select
                value={composeForm.sender_email}
                onValueChange={(value) => setComposeForm({ ...composeForm, sender_email: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select sender" />
                </SelectTrigger>
                <SelectContent>
                  {senders.map((sender) => (
                    <SelectItem key={sender.email} value={sender.email}>
                      {sender.name} ({sender.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendTestEmail} 
              className="bg-[#2C9DE1] hover:bg-[#1A7BB8]"
              disabled={sendingTest}
            >
              {sendingTest && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Send Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Stats Dialog */}
      <Dialog open={!!selectedCampaign} onOpenChange={() => { setSelectedCampaign(null); setCampaignRecipients(null); setRecipientFilter('all'); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Campaign Details
            </DialogTitle>
          </DialogHeader>
          {selectedCampaign && (
            <div className="space-y-6">
              <div>
                <p className="font-semibold text-lg">{selectedCampaign.name}</p>
                <p className="text-sm text-slate-500">{selectedCampaign.subject}</p>
              </div>
              
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-xl font-bold text-slate-900">{campaignStats?.sent_count || 0}</p>
                  <p className="text-xs text-slate-500">Sent</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-xl font-bold text-green-600">{campaignStats?.opened_count || 0}</p>
                  <p className="text-xs text-slate-500">Opened ({campaignStats?.open_rate || 0}%)</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-xl font-bold text-blue-600">{campaignStats?.clicked_count || 0}</p>
                  <p className="text-xs text-slate-500">Clicked ({campaignStats?.click_rate || 0}%)</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg text-center">
                  <p className="text-xl font-bold text-red-600">{campaignStats?.failed_count || 0}</p>
                  <p className="text-xs text-slate-500">Failed</p>
                </div>
              </div>

              {/* Recipients List */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-sm">Recipients</h4>
                  <Select
                    value={recipientFilter}
                    onValueChange={(value) => {
                      setRecipientFilter(value);
                      loadCampaignRecipients(selectedCampaign.id, value);
                    }}
                  >
                    <SelectTrigger className="w-36 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Recipients</SelectItem>
                      <SelectItem value="opened">Opened</SelectItem>
                      <SelectItem value="clicked">Clicked</SelectItem>
                      <SelectItem value="not_opened">Not Opened</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {loadingRecipients ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : campaignRecipients?.items?.length > 0 ? (
                  <div className="border rounded-lg max-h-60 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="text-xs">
                          <TableHead className="py-2">Recipient</TableHead>
                          <TableHead className="py-2 text-center">Opened</TableHead>
                          <TableHead className="py-2 text-center">Clicked</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {campaignRecipients.items.map((recipient) => (
                          <TableRow key={recipient.id} className="text-sm">
                            <TableCell className="py-2">
                              <div>
                                <p className="font-medium text-xs">{recipient.name}</p>
                                <p className="text-xs text-slate-500">{recipient.email}</p>
                              </div>
                            </TableCell>
                            <TableCell className="py-2 text-center">
                              {recipient.opened ? (
                                <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </TableCell>
                            <TableCell className="py-2 text-center">
                              {recipient.clicked ? (
                                <div className="flex items-center justify-center gap-1">
                                  <CheckCircle className="w-4 h-4 text-blue-500" />
                                  {recipient.click_count > 1 && (
                                    <span className="text-xs text-blue-600">x{recipient.click_count}</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-center text-sm text-slate-500 py-4">No recipients found</p>
                )}
                
                {campaignRecipients && campaignRecipients.total > campaignRecipients.items?.length && (
                  <p className="text-xs text-slate-500 text-center mt-2">
                    Showing {campaignRecipients.items?.length} of {campaignRecipients.total} recipients
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Preview */}
      <EmailPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        subject={composeForm.subject}
        htmlBody={composeForm.html_body}
        senderEmail={composeForm.sender_email}
      />

      {/* Sender Address Dialog */}
      <Dialog open={senderDialogOpen} onOpenChange={setSenderDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingSender ? 'Edit Sender' : 'Add Sender'}
            </DialogTitle>
            <DialogDescription>
              {editingSender 
                ? 'Update sender address details'
                : 'Add a new sender address for marketing emails'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveSender} className="space-y-4" data-testid="sender-form">
            <div className="space-y-2">
              <Label htmlFor="sender_name">Name *</Label>
              <Input
                id="sender_name"
                data-testid="sender-name-input"
                value={senderForm.name}
                onChange={(e) => setSenderForm({ ...senderForm, name: e.target.value })}
                placeholder="e.g., Chris Sherri"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sender_email">Email Address *</Label>
              <Input
                id="sender_email"
                data-testid="sender-email-input"
                type="email"
                value={senderForm.email}
                onChange={(e) => setSenderForm({ ...senderForm, email: e.target.value })}
                placeholder="e.g., chris@empmedic.com"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="is_default"
                data-testid="sender-default-checkbox"
                checked={senderForm.is_default}
                onCheckedChange={(checked) => setSenderForm({ ...senderForm, is_default: checked })}
              />
              <Label htmlFor="is_default" className="text-sm font-normal cursor-pointer">
                Set as default sender
              </Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSenderDialogOpen(false)} data-testid="sender-cancel-btn">
                Cancel
              </Button>
              <Button type="submit" className="bg-[#2C9DE1] hover:bg-[#1A7BB8]" disabled={savingSender} data-testid="sender-submit-btn">
                {savingSender && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingSender ? 'Update' : 'Add Sender'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailMarketing;
